import type { CalendarBar, PlatformLegendEntry, SalesCycle } from '@complan/contracts';
import {
  barDateRange,
  barGeometryOnTrack,
  buildCalendarWeekTrack,
  buildMonthTrack,
  buildSalesCycleTrack,
  isoWeekOf,
  parseCalendarDate,
  toCalendarDate,
  type CalendarDate,
  type CalendarOrientation,
  type CalendarTrack,
  type SalesCycleColumnDefinition,
  type SalesCycleTrack,
} from '@complan/domain';
import { i18n } from '../../shared/i18n';
import { formatIsoWeekLabel } from '../../shared/format/date-format';

/**
 * The pure, framework-free model of a Calendar_View grid (task 22.2).
 *
 * Everything the grid renders is derived here from the cached server payload —
 * the bars, the Platform legend and the year's Sales_Cycle definitions — plus
 * the selected orientation and the current system date. Nothing here touches the
 * DOM, the clock or the network: the current date arrives as an argument, so the
 * "only when the selected year is the current year" rule of R10.12 is a pure
 * function of its inputs and is trivially testable.
 *
 * The load-bearing property is that {@link buildCalendarGrid} is a *re-derivation*
 * over data that does not change between orientations. Switching orientation
 * re-runs this builder against the same cached `bars`/`legend`/`cycles`; it never
 * triggers a fetch (R10.2). All date arithmetic — the month track, the ISO week
 * grid, the Sales_Cycle track — comes from `@complan/domain`; the web layer owns
 * no calendar maths of its own.
 *
 * ## Seams for later tasks
 *
 * This task builds the grid *scaffolding*: the header, the Sales Cycle band, the
 * Platform lane rows and the current-period indicators. It intentionally stops
 * short of positioning Initiative bars and milestone markers inside the lanes —
 * that is the bar/milestone layer of task 22.3, which will consume
 * {@link PlatformLane.bars} (the bars already partitioned per Platform lane here)
 * plus the domain geometry and lane-assignment functions. The bars are grouped
 * into lanes at this layer only so 22.3 has a clean, per-lane input; their pixel
 * geometry is deliberately not computed yet.
 */

/** The lane-label column of UI spec §4.1: `150px repeat(12, 1fr)`. */
export const LANE_LABEL_COLUMN_PX = 150;

/** One header/axis column: a month, an ISO week, or a Sales_Cycle. */
export interface GridColumn {
  /** Position on the axis, 0-based. */
  readonly index: number;
  /** The short header label (e.g. `JAN`, `W01`, a cycle code). */
  readonly label: string;
  /** A longer descriptive label for the accessible name / tooltip. */
  readonly fullLabel: string;
  /** First day this column covers. */
  readonly start: CalendarDate;
  /** Last day this column covers, inclusive. */
  readonly end: CalendarDate;
}

/**
 * One Sales Cycle band chip (UI spec §4.1).
 *
 * Positioned in *fractional* column units, the same measure the Initiative bars
 * use ({@link barGeometryOnTrack}): `startColumns` is the chip's left edge and
 * `widthColumns` its width, where `0` is the track's left edge and `columnCount`
 * its right edge. A cycle ending on 01 March therefore stops one day into the
 * March column rather than filling it - which is what an earlier whole-column
 * `gridColumn` span could not express, since it snapped to any column the range
 * merely overlapped.
 *
 * The renderer places the chip with a left/width percentage over the lane track,
 * exactly as it places a bar; the model computes the fractions from the cycle's
 * real dates so the two cannot disagree.
 */
export interface SalesCycleBandChip {
  readonly salesCycleId: string;
  readonly label: string;
  readonly fullLabel: string;
  /** Left edge in column units, measured from the track's left edge. */
  readonly startColumns: number;
  /** Width in column units. */
  readonly widthColumns: number;
  /** Total columns in the track, so the renderer can turn the above into a percentage. */
  readonly columnCount: number;
}

/** One Platform lane row (UI spec §4.1: one row per Platform). */
export interface PlatformLane {
  readonly platformId: string;
  /** The short Platform code shown in the lane label and legend (R10.5, R10.6). */
  readonly code: string;
  readonly name: string;
  /** The Platform colour from master data (R10.5); the bar layer fills bars with it. */
  readonly colour: string;
  /**
   * The bars whose primary Platform is this lane's Platform, in the server's
   * order. Positioning and stacking are the bar layer's job (task 22.3); grouping
   * them per lane here gives that layer a clean input.
   */
  readonly bars: readonly CalendarBar[];
}

/**
 * The current-period indicators of R10.12, present only when the selected
 * planning year equals the current system year.
 *
 * `currentMonthColumnIndex` and `currentIsoWeekColumnIndex` are axis-column
 * positions in the *selected* orientation, or `null` when the orientation has no
 * such column (a month has no ISO-week column in Sales_Cycle orientation, and so
 * on). When the years differ, {@link CurrentPeriod} is `null` entirely so the
 * renderer draws no indicator at all (R10.12).
 */
export interface CurrentPeriod {
  /** The axis column holding today's month, or `null` if the axis has no month column. */
  readonly currentMonthColumnIndex: number | null;
  /** The axis column holding today's ISO week, or `null` if the axis has no week column. */
  readonly currentIsoWeekColumnIndex: number | null;
}

/** The fully derived grid model consumed by the grid components. */
export interface CalendarGridModel {
  readonly orientation: CalendarOrientation;
  readonly planningYear: number;
  /** The axis columns, left to right. 12 months, 52/53 weeks, or N cycles. */
  readonly columns: readonly GridColumn[];
  /** The Sales Cycle band chips, positioned over the axis columns. */
  readonly salesCycleBand: readonly SalesCycleBandChip[];
  /** One lane per primary Platform present in the rendering (R10.5). */
  readonly platformLanes: readonly PlatformLane[];
  /** The R10.12 indicators, or `null` when the selected year is not the current year. */
  readonly currentPeriod: CurrentPeriod | null;
  /** Distinct readable Initiatives in scope (R11.9); may exceed the bar count. */
  readonly count: number;
  /** True when nothing is rendered: the empty-result case of R10.16. */
  readonly isEmpty: boolean;
  /**
   * The day-proportional domain track (month / calendar-week), or `undefined` in
   * Sales_Cycle orientation. The bar/milestone layer (task 22.3) positions bars
   * against this via the domain geometry functions; the grid model itself
   * computes no pixel geometry.
   */
  readonly dayTrack: CalendarTrack | undefined;
  /** The Sales_Cycle domain track in Sales_Cycle orientation, else `undefined`. */
  readonly salesCycleTrack: SalesCycleTrack | undefined;
}

/** The inputs {@link buildCalendarGrid} derives a model from — all from cache. */
export interface CalendarGridInput {
  readonly orientation: CalendarOrientation;
  readonly planningYear: number;
  readonly bars: readonly CalendarBar[];
  readonly legend: readonly PlatformLegendEntry[];
  readonly count: number;
  /** The active Sales_Cycle definitions of the planning year (R6.1, R7.2). */
  readonly cycles: readonly SalesCycle[];
  /** Today, injected so R10.12 stays a pure function of its inputs. */
  readonly today: Date;
}

/**
 * Builds the grid model for the selected orientation from cached data.
 *
 * Pure and total: given the same inputs it returns the same model (R18.14), and
 * it never fetches. The three orientations differ only in how the axis columns
 * and the Sales Cycle band are derived; the Platform lanes and the R10.12
 * indicators are computed the same way for all three.
 */
export function buildCalendarGrid(input: CalendarGridInput): CalendarGridModel {
  const { orientation, planningYear } = input;

  const cycleDefinitions = toCycleDefinitions(input.cycles, planningYear);

  const axis = buildAxis(orientation, planningYear, cycleDefinitions, input.cycles);
  const salesCycleBand = buildSalesCycleBand(axis, cycleDefinitions, input.cycles);
  const platformLanes = buildPlatformLanes(input.legend, input.bars);
  const currentPeriod = buildCurrentPeriod(orientation, planningYear, axis.columns, input.today);

  return {
    orientation,
    planningYear,
    columns: axis.columns,
    salesCycleBand,
    platformLanes,
    currentPeriod,
    count: input.count,
    isEmpty: input.count === 0 && input.bars.length === 0,
    dayTrack: axis.dayTrack,
    salesCycleTrack: axis.salesCycleTrack,
  };
}

/** The axis columns plus the domain track they were derived from. */
interface Axis {
  readonly columns: readonly GridColumn[];
  /**
   * The day-proportional track for month / calendar-week orientations, used to
   * map Sales_Cycle date ranges onto axis columns. `undefined` in Sales_Cycle
   * orientation, whose axis *is* the cycles.
   */
  readonly dayTrack: CalendarTrack | undefined;
  /** The Sales_Cycle track in Sales_Cycle orientation, else `undefined`. */
  readonly salesCycleTrack: SalesCycleTrack | undefined;
}

function buildAxis(
  orientation: CalendarOrientation,
  planningYear: number,
  cycleDefinitions: readonly SalesCycleColumnDefinition[],
  cycles: readonly SalesCycle[],
): Axis {
  switch (orientation) {
    case 'month': {
      const track = buildMonthTrack(planningYear);
      return {
        columns: track.columns.map((column) => {
          const monthNumber = column.index + 1;
          return {
            index: column.index,
            label: i18n.t(`month.short.${String(monthNumber)}`),
            fullLabel: `${i18n.t(`month.long.${String(monthNumber)}`)} ${String(planningYear)}`,
            start: column.start,
            end: column.end,
          };
        }),
        dayTrack: track,
        salesCycleTrack: undefined,
      };
    }
    case 'calendar-week': {
      const track = buildCalendarWeekTrack(planningYear);
      return {
        columns: track.columns.map((column, index) => ({
          index,
          // The ISO week axis label comes from the shared formatter (R18.10).
          label: formatIsoWeekLabel(index + 1),
          fullLabel: i18n.t('week.fullLabel', { week: index + 1, year: planningYear }),
          start: column.start,
          end: column.end,
        })),
        dayTrack: track,
        salesCycleTrack: undefined,
      };
    }
    case 'sales-cycle': {
      const track = buildSalesCycleTrack(planningYear, cycleDefinitions);
      const names = new Map(cycles.map((cycle) => [cycle.id, cycle.name]));
      return {
        columns: track.columns.map((column) => {
          // The axis header shows the cycle's own name from master data (the
          // name the author gave it). The positional `SC1`/`SC2` code is only a
          // fallback for a column whose cycle name is unavailable.
          const name = names.get(column.salesCycleId) ?? cycleLabel(column.index);
          return {
            index: column.index,
            label: name,
            fullLabel: name,
            start: column.start,
            end: column.end,
          };
        }),
        dayTrack: undefined,
        salesCycleTrack: track,
      };
    }
  }
}

/**
 * The Sales Cycle band chips (UI spec §4.1).
 *
 * In the day-proportional orientations (month, calendar-week) each active cycle
 * is positioned by its real date range through {@link barGeometryOnTrack} - the
 * same geometry the Initiative bars use - so a cycle that ends on 01 March stops
 * one day into the March column rather than filling it. This replaced an earlier
 * whole-column overlap span, which snapped a chip to every column its range
 * merely touched and so overstated a cycle that ended early in a column.
 *
 * In Sales_Cycle orientation the axis columns *are* the cycles, so each chip fills
 * exactly its own column, index `i` to `i + 1`.
 */
function buildSalesCycleBand(
  axis: Axis,
  cycleDefinitions: readonly SalesCycleColumnDefinition[],
  cycles: readonly SalesCycle[],
): readonly SalesCycleBandChip[] {
  const names = new Map(cycles.map((cycle) => [cycle.id, cycle.name]));

  if (axis.salesCycleTrack) {
    const columnCount = axis.salesCycleTrack.columns.length;
    return axis.salesCycleTrack.columns.map((column) => {
      const name = names.get(column.salesCycleId) ?? cycleLabel(column.index);
      return {
        salesCycleId: column.salesCycleId,
        label: name,
        fullLabel: name,
        startColumns: column.index,
        widthColumns: 1,
        columnCount,
      };
    });
  }

  const track = axis.dayTrack;
  if (!track) return [];

  const chips: SalesCycleBandChip[] = [];
  cycleDefinitions.forEach((cycle, ordinal) => {
    // The domain clips the range to the track and measures it in fractional
    // columns, exactly as it does for a bar; a cycle entirely off the rendered
    // track yields `undefined` and is dropped.
    const geometry = barGeometryOnTrack(track, barDateRange(cycle.startDate, cycle.endDate));
    if (!geometry) return;
    const name = names.get(cycle.salesCycleId) ?? cycleLabel(ordinal);
    chips.push({
      salesCycleId: cycle.salesCycleId,
      label: name,
      fullLabel: name,
      startColumns: geometry.startColumns,
      widthColumns: geometry.widthColumns,
      columnCount: geometry.columnCount,
    });
  });
  return chips;
}

/** One lane per legend Platform, with its bars grouped in (R10.5). */
function buildPlatformLanes(
  legend: readonly PlatformLegendEntry[],
  bars: readonly CalendarBar[],
): readonly PlatformLane[] {
  const barsByPlatform = new Map<string, CalendarBar[]>();
  for (const bar of bars) {
    const group = barsByPlatform.get(bar.primaryPlatformId);
    if (group) {
      group.push(bar);
    } else {
      barsByPlatform.set(bar.primaryPlatformId, [bar]);
    }
  }
  return legend.map((entry) => ({
    platformId: entry.platformId,
    code: entry.code,
    name: entry.name,
    colour: entry.colour,
    bars: barsByPlatform.get(entry.platformId) ?? [],
  }));
}

/**
 * The current-period indicators (R10.12).
 *
 * Returns `null` — no indicators at all — unless the selected planning year is
 * the current system year. When it is, the month and ISO-week column indices are
 * resolved against the selected orientation's axis, which is why a month
 * orientation yields a month indicator and no week indicator, and vice versa.
 */
function buildCurrentPeriod(
  orientation: CalendarOrientation,
  planningYear: number,
  columns: readonly GridColumn[],
  today: Date,
): CurrentPeriod | null {
  const todayDate = parseCalendarDate(isoDateString(today));
  if (todayDate.year !== planningYear) {
    // The current *month* is unambiguously in the selected year here. The ISO
    // week, however, can belong to a neighbouring ISO year (1–3 January), so the
    // year check uses the calendar year, matching R10.12's "planning year of the
    // current system date".
    return null;
  }

  if (orientation === 'month') {
    return {
      currentMonthColumnIndex: todayDate.month - 1,
      currentIsoWeekColumnIndex: null,
    };
  }

  if (orientation === 'calendar-week') {
    const reference = isoWeekOf(todayDate);
    const index =
      reference.isoYear === planningYear
        ? columns.findIndex((column) => column.index === reference.isoWeek - 1)
        : -1;
    return {
      currentMonthColumnIndex: null,
      currentIsoWeekColumnIndex: index === -1 ? null : index,
    };
  }

  // Sales_Cycle orientation: the axis is the cycles. Highlight the cycle column
  // whose date range contains today, if any.
  const index = columns.findIndex(
    (column) => compare(column.start, todayDate) <= 0 && compare(column.end, todayDate) >= 0,
  );
  return {
    currentMonthColumnIndex: null,
    currentIsoWeekColumnIndex: index === -1 ? null : index,
  };
}

/** Active cycles of the planning year, as the track builder needs them. */
function toCycleDefinitions(
  cycles: readonly SalesCycle[],
  planningYear: number,
): readonly SalesCycleColumnDefinition[] {
  return cycles
    .filter((cycle) => cycle.isActive && cycle.planningYear === planningYear)
    .map((cycle) => ({
      salesCycleId: cycle.id,
      startDate: toCalendarDate(cycle.startDate),
      endDate: toCalendarDate(cycle.endDate),
    }));
}

function compare(a: CalendarDate, b: CalendarDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/** `Date` → `YYYY-MM-DD` in the runtime's local zone, matching the wall clock the user sees. */
function isoDateString(date: Date): string {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  return `${String(year)}-${month}-${day}`;
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * The positional Sales_Cycle column code (`SC1`, `SC2`, …). Used only as a
 * fallback for a column whose cycle name is unavailable; the axis and band
 * normally show the cycle's own name from master data.
 */
function cycleLabel(index: number): string {
  return `SC${String(index + 1)}`;
}
